import React from "react";
import { FiAlertTriangle, FiRefreshCw } from "react-icons/fi";

/**
 * There was no error boundary anywhere in this app: one bad render — a null
 * room, an unexpected action shape — blanked the entire dashboard to a white
 * screen with only a console trace to go on.
 *
 * Wraps the admin shell and each tab, so a crash in one panel leaves the rest
 * of the page usable.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("💥 Render error:", error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="mx-auto my-8 max-w-lg rounded-xl border border-amber-200 bg-amber-50/70 p-8 text-center">
        <FiAlertTriangle className="mx-auto text-amber-500" size={36} />
        <h3 className="mt-3 text-base font-semibold text-amber-900">
          This section failed to render
        </h3>
        <p className="mt-1 text-sm text-amber-800 break-words">
          {error?.message || "An unexpected error occurred."}
        </p>
        <button
          type="button"
          onClick={this.reset}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
        >
          <FiRefreshCw size={14} />
          Try again
        </button>
      </div>
    );
  }
}
